import { createContext, useContext } from "react";
import { calculatePercentage, round, calculateSubtractedValues, calculateSum, decimalRound } from "../../libraries/_helpers";
import { isObjectEmpty } from "../mls-shooting-breakdown/utils";
import { isArrayEmpty } from "../mls-possession/utils";

export const StatsContext = createContext();
StatsContext.displayName = 'Stats';

export function useStatsData() {
  const context = useContext(StatsContext);
  if (context === undefined) {
    throw new Error('useStatsData must be used within a <PlayerStats /> or <ClubStats/>');
  }
  return context;
}

export function formatSeasonStatsData(data) {
 
  if (isObjectEmpty(data) || !data?.hasOwnProperty("player_statistics") || isArrayEmpty(data?.player_statistics)) {
    return [];
  }
  
  return data.player_statistics
  .map(el => {
    const {
      goals,
      first_and_second_assists,
      assists: goal_assist,
      shots_conversion_rate: total_scoring_att,
      shots_on_target: ontarget_scoring_att,
      passes_successful_sum: accurate_pass,
      goalkeeper_saves: saves,
      passes_from_open_play_long,
      passes_from_open_play_long_successful_ratio,
      clearance,
      penalties_saved: penalty_save,
      penalty_faced,
      normalized_player_minutes: mins_played,
      goals_conceded,
      clean_sheet,
      interceptions_sum: interception,
      attempts_conceded_ibox,
      expected_goals_conceded,
      attempts_conceded_obox,
      expected_assists,
      assists_shot_at_goal: total_att_assist,
      accurate_fwd_zone_pass,
      accurate_back_zone_pass,
      crosses_successful_sum: accurate_cross,
      crosses_conversion_rate: accurate_cross_per,
      passes_from_open_play_short: successful_short_pass,
      accurate_launches,
      failed_launches,
      xG: expected_goals,
      shots_at_goal_inside: att_ibox_goal,
      shots_at_goal_outside_box: att_obox_goal,
      shots_at_goal_head: att_hd_goal,
      penalties_successful: att_pen_goal,
      penalties_sum: att_pen_taken,
      passes_sum: total_pass,
      offsides: total_offside,
      matches_played: games_played,
      turnover,
      cards_yellow: yellow_card,
      cards_red: total_red_card,
      fouls_sum: fk_foul_lost,
      fouls_suffered: fk_foul_won,
      corner_kicks_sum: corner_taken,
      duel_won,
      tackling_games_air_sum: aerial_duel,
      duel,
      shots_conversion_rate: accurate_shooting_per,
      team_short_name: name,
      passes_from_play_conversion_rate,
      season: date,
      free_kicks_sum: att_freekick_total,
      shots_at_goal_left_leg: att_lf_total,
      shots_at_goal_right_leg: att_rf_total,
      shots_at_goal_head: att_hd_total,
      defensive_clearances: total_clearance,
      tackling_games_air_won: aerial_won,
      substitutions_in
    } = el || {};

    return {
      date,
      name: name ? name : null,
      games_played: games_played ? games_played : 0,
      game_started: games_played - substitutions_in < 0 ? 0 : games_played - substitutions_in,
      expected_goals_minus_goals: round(calculateSubtractedValues(expected_goals, goals), 1),
      att_ibox_goal: att_ibox_goal ? att_ibox_goal : 0,
      att_obox_goal: att_obox_goal ? att_obox_goal : 0,
      att_hd_goal: att_hd_goal ? att_hd_goal : 0,
      att_pen_goal: att_pen_goal ? att_pen_goal : 0,
      att_pen_taken: att_pen_taken ? att_pen_taken : 0,
      att_pen_percentage: calculatePercentage(att_pen_goal, att_pen_taken),
      accurate_shooting_per: accurate_shooting_per || calculatePercentage(goals, total_scoring_att),
      goals_assists: first_and_second_assists,
      expected_goals_assists: round(calculateSum(expected_goals, expected_assists), 1),
      expected_assists_minus_assists: round(calculateSubtractedValues(expected_assists, goal_assist), 1),
      launches_attempted: calculateSum(accurate_launches, failed_launches),
      shots_against: calculateSum(attempts_conceded_ibox, attempts_conceded_obox),
      headed_duel_percentage: calculatePercentage(aerial_duel, duel),
      ground_duel_percentage: calculatePercentage(duel_won, duel),
      long_ball_percentage: calculatePercentage(passes_from_open_play_long_successful_ratio, 100),
      penalty_kick_save_percentage: calculatePercentage(penalty_save, penalty_faced),
      save_percentage: calculatePercentage(saves, calculateSum(goals_conceded, saves)),
      saves: saves ? saves : 0,
      clearance: clearance ? clearance : 0,
      penalty_faced: penalty_faced ? penalty_faced : 0,
      penalty_save: penalty_save ? penalty_save : 0,
      interception: interception ? interception : 0,
      clean_sheet: clean_sheet ? clean_sheet : 0,
      expected_goals_conceded: expected_goals_conceded ? round(expected_goals_conceded, 1) : 0,
      successful_short_pass: successful_short_pass ? successful_short_pass : 0,
      accurate_long_balls:  passes_from_open_play_long && passes_from_open_play_long_successful_ratio ? 
        roundNumber(passes_from_open_play_long * passes_from_open_play_long_successful_ratio / 100)  : 0,
      expected_assists: expected_assists ? round(expected_assists, 1) : 0,
      total_att_assist: total_att_assist ? total_att_assist : 0,
      accurate_pass: accurate_pass ? accurate_pass : 0,
      total_pass: total_pass ? total_pass : 0,
      accurate_fwd_zone_pass: accurate_fwd_zone_pass ? accurate_fwd_zone_pass : 0,
      accurate_back_zone_pass: accurate_back_zone_pass ? accurate_back_zone_pass : 0,
      accurate_cross: accurate_cross ? accurate_cross : 0,
      mins_played: mins_played ? mins_played : 0,
      goals: goals ? goals : 0,
      goal_assist: goal_assist ? goal_assist : 0,
      total_scoring_att: total_scoring_att ? total_scoring_att : 0,
      ontarget_scoring_att: ontarget_scoring_att ? ontarget_scoring_att : 0,
      accurate_pass_per: passes_from_play_conversion_rate,
      total_offside: total_offside ? total_offside : 0,
      turnover: turnover ? turnover : 0,
      goals_conceded: goals_conceded ? round(goals_conceded, 1) : 0,
      yellow_card: yellow_card ? yellow_card : 0,
      total_red_card: total_red_card ? total_red_card : 0,
      fk_foul_lost: fk_foul_lost ? fk_foul_lost : 0,
      fk_foul_won: fk_foul_won ? fk_foul_won : 0,
      corner_taken: corner_taken ? corner_taken : 0,
      att_freekick_total: att_freekick_total || 0,
      att_rf_total: att_rf_total || 0,
      att_lf_total: att_lf_total || 0,
      att_hd_total: att_hd_total || 0,
      expected_goals: decimalRound(expected_goals) || 0,
      aerial_duel: aerial_duel || 0,
      aerial_duel_per: calculatePercentage(aerial_won, aerial_duel),
      total_clearance: total_clearance || 0,
      accurate_cross_per: accurate_cross_per || 0,
    };
  });
}

function roundNumber(value) {
  if (!value || typeof value !== 'number') return 0;
 
  const decimalValue = +value.toString().split('.')[1] || 0;
  let decimalPlaces = 0;
  if (decimalValue >= 10) decimalPlaces = 2;
  if (decimalValue > 0 && decimalValue < 10) decimalPlaces = 1;

  return value.toFixed(decimalPlaces);
}

export const averageValues = {
  accurate_pass_per: true,
  accurate_shooting_per: true,
  att_pen_percentage: true,
  att_pen_taken: true,
  ground_duel_percentage: true,
  headed_duel_percentage: true,
  accurate_long_balls: true,
  penalty_kick_save_percentage: true,
  save_percentage: true,
};

